import * as yup from "yup";

// Common validation patterns
const commonPatterns = {
  email: yup
    .string()
    .email("Please enter a valid email address")
    .max(255, "Email must be less than 255 characters")
    .required("Email is required"),

  password: yup
    .string()
    .min(6, "Password must be at least 6 characters")
    .max(128, "Password must be less than 128 characters")
    .required("Password is required"),

  username: yup
    .string()
    .trim()
    .matches(
      /^[a-zA-Z0-9_]+$/,
      "Pen name can only contain letters, numbers, and underscores"
    )
    .min(3, "Pen name must be at least 3 characters")
    .max(30, "Pen name must be less than 30 characters")
    .required("Pen name is required"),

  objectId: yup
    .string()
    .matches(/^[0-9a-fA-F]{24}$/, "Invalid ID format")
    .required("ID is required"),

  text: yup.string().max(1000, "Text must be less than 1000 characters"),

  longText: yup.string().max(10000, "Text must be less than 10000 characters"),

  boolean: yup.boolean(),

  positiveInteger: yup
    .number()
    .integer("Must be a whole number")
    .min(1, "Must be at least 1")
    .required(),

  nonNegativeInteger: yup
    .number()
    .integer("Must be a whole number")
    .min(0, "Cannot be negative")
    .required(),

  page: yup
    .number()
    .integer("Must be a whole number")
    .min(1, "Page must be at least 1")
    .default(1),

  limit: yup
    .number()
    .integer("Must be a whole number")
    .min(1, "Limit must be at least 1")
    .max(100, "Limit cannot exceed 100")
    .default(10),
};

// Authentication schemas
export const authSchemas = {
  register: yup.object({
    username: commonPatterns.username,
    name: yup
      .string()
      .trim()
      .max(100, "Name must be less than 100 characters")
      .optional()
      .default(""),
    email: commonPatterns.email,
    password: commonPatterns.password,
    confirmPassword: yup
      .string()
      .oneOf([yup.ref("password"), null], "Passwords must match")
      .required("Please confirm your password"),
  }),

  login: yup.object({
    emailOrUsername: yup
      .string()
      .trim()
      .required("Email or Pen name is required")
      .min(1, "Email or Pen name cannot be empty"),
    password: yup
      .string()
      .trim()
      .required("Password is required")
      .min(1, "Password cannot be empty"),
  }),

  verifyEmail: yup.object({
    email: commonPatterns.email,
    otp: yup
      .string()
      .length(6, "OTP must be exactly 6 digits")
      .matches(/^\d+$/, "OTP must contain only numbers")
      .required("OTP is required"),
  }),

  forgotPassword: yup.object({
    email: commonPatterns.email,
  }),

  resetPassword: yup.object({
    email: commonPatterns.email,
    resetToken: yup.string().required("Reset token is required"),
    newPassword: commonPatterns.password,
    confirmPassword: yup
      .string()
      .oneOf([yup.ref("newPassword"), null], "Passwords must match")
      .required("Please confirm your password"),
  }),

  changePassword: yup.object({
    currentPassword: yup.string().required("Current password is required"),
    newPassword: commonPatterns.password,
    confirmPassword: yup
      .string()
      .oneOf([yup.ref("newPassword"), null], "Passwords must match")
      .required("Please confirm your password"),
  }),
};

// User schemas
export const userSchemas = {
  updateProfile: yup.object({
    username: commonPatterns.username.optional(),
    email: commonPatterns.email.optional(),
    name: yup
      .string()
      .max(100, "Name must be less than 100 characters")
      .transform((value) => {
        // Convert empty string to undefined to allow clearing the name
        return value === "" ? undefined : value;
      })
      .optional(),
    bio: yup
      .string()
      .max(500, "Bio must be less than 500 characters")
      .optional(),
    avatar: yup.string().url("Please enter a valid URL").optional(),
  }),

  followUser: yup.object({
    userId: commonPatterns.objectId,
  }),
};

// Poem schemas
export const poemSchemas = {
  createPoem: yup.object({
    title: yup
      .string()
      .min(1, "Title is required")
      .max(200, "Title must be less than 200 characters")
      .required("Title is required"),
    content: yup
      .string()
      .min(1, "Content is required")
      .max(50000, "Content must be less than 50000 characters")
      .required("Content is required"),
    isDraft: commonPatterns.boolean.optional(),
    alignment: yup
      .string()
      .oneOf(["left", "center", "right", "justify"], "Invalid alignment")
      .optional(),
    font: yup
      .string()
      .oneOf(["serif", "sans", "mono"], "Invalid font")
      .optional(),
    tags: yup
      .string()
      .max(500, "Tags must be less than 500 characters")
      .optional(),
    copyrightEnabled: commonPatterns.boolean.optional(),
  }),

  updatePoem: yup.object({
    title: yup
      .string()
      .min(1, "Title is required")
      .max(200, "Title must be less than 200 characters")
      .optional(),
    content: yup
      .string()
      .min(1, "Content is required")
      .max(50000, "Content must be less than 50000 characters")
      .optional(),
    isDraft: commonPatterns.boolean.optional(),
    alignment: yup
      .string()
      .oneOf(["left", "center", "right", "justify"], "Invalid alignment")
      .optional(),
    font: yup
      .string()
      .oneOf(["serif", "sans", "mono"], "Invalid font")
      .optional(),
    tags: yup
      .string()
      .max(500, "Tags must be less than 500 characters")
      .optional(),
    copyrightEnabled: commonPatterns.boolean.optional(),
  }),

  getPoems: yup.object({
    page: commonPatterns.page,
    limit: commonPatterns.limit,
    author: commonPatterns.objectId.optional(),
    isDraft: commonPatterns.boolean.optional(),
    search: yup
      .string()
      .max(100, "Search term must be less than 100 characters")
      .optional(),
  }),

  reportPoem: yup.object({
    reason: yup
      .string()
      .min(1, "Report reason is required")
      .max(500, "Report reason must be less than 500 characters")
      .required("Report reason is required"),
  }),
};

// Comment schemas
export const commentSchemas = {
  createComment: yup.object({
    content: yup
      .string()
      .min(1, "Comment is required")
      .max(1000, "Comment must be less than 1000 characters")
      .required("Comment is required"),
  }),

  updateComment: yup.object({
    content: yup
      .string()
      .min(1, "Comment is required")
      .max(1000, "Comment must be less than 1000 characters")
      .required("Comment is required"),
  }),

  createReply: yup.object({
    content: yup
      .string()
      .min(1, "Reply is required")
      .max(1000, "Reply must be less than 1000 characters")
      .required("Reply is required"),
  }),

  updateReply: yup.object({
    content: yup
      .string()
      .min(1, "Reply is required")
      .max(1000, "Reply must be less than 1000 characters")
      .required("Reply is required"),
  }),
};

// Admin schemas
export const adminSchemas = {
  updateUserRole: yup.object({
    userId: commonPatterns.objectId,
    role: yup
      .string()
      .oneOf(["user", "moderator", "admin"], "Invalid role")
      .required("Role is required"),
  }),

  suspendUser: yup.object({
    userId: commonPatterns.objectId,
    reason: yup
      .string()
      .min(1, "Suspension reason is required")
      .max(500, "Suspension reason must be less than 500 characters")
      .required("Suspension reason is required"),
    duration: commonPatterns.positiveInteger.optional(),
  }),
};

// Feedback schemas
export const feedbackSchemas = {
  submitFeedback: yup.object({
    subject: yup
      .string()
      .min(1, "Subject is required")
      .max(200, "Subject must be less than 200 characters")
      .required("Subject is required"),
    message: yup
      .string()
      .min(1, "Message is required")
      .max(2000, "Message must be less than 2000 characters")
      .required("Message is required"),
    email: commonPatterns.email,
  }),
};

// Chat schemas
export const chatSchemas = {
  sendMessage: yup.object({
    content: yup
      .string()
      .min(1, "Message is required")
      .max(1000, "Message must be less than 1000 characters")
      .required("Message is required"),
    recipientId: commonPatterns.objectId.optional(),
  }),

  getMessages: yup.object({
    page: commonPatterns.page,
    limit: commonPatterns.limit,
    userId: commonPatterns.objectId.optional(),
  }),
};

// Export all schemas
export default {
  auth: authSchemas,
  user: userSchemas,
  poem: poemSchemas,
  comment: commentSchemas,
  admin: adminSchemas,
  feedback: feedbackSchemas,
  chat: chatSchemas,
  common: commonPatterns,
};
